/**
 * AuthContext — Firebase Authentication with Google sign-in.
 *
 * Flow:
 *   1. Fetch Firebase config from /api/auth/config
 *   2. Initialize Firebase client SDK
 *   3. Listen to auth state changes
 *   4. Ask /api/auth/me whether the signed-in account may use the dashboard,
 *      and which sections it may see
 *   5. Provide user / allowedSections / signIn / signOut to the app
 *
 * The server decides access, not this file. The email domain is only one of the
 * ways access is granted — invited addresses outside the domain are valid too —
 * so rejecting on domain here would lock out exactly the users we invited. The
 * same `/api/auth/me` response drives navigation, which keeps the menu in step
 * with what the API will actually serve.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { initFirebase, getFirebaseAuth, type FirebaseConfig } from '@/lib/firebase';
import { ALL_SECTION_IDS } from '@/lib/sections';
import { api, setApiTokenProvider, ApiRequestError } from '@/services/api';
import { endpoints } from '@/services/endpoints';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AuthConfigResponse {
  firebaseConfig: FirebaseConfig | null;
  allowedEmailDomain: string;
  firestoreRootCollection: string;
  devMode?: boolean;
}

interface MeResponse {
  email: string;
  sections: string[];
  fullAccess: boolean;
}

interface AuthContextValue {
  /** The currently authenticated user (null if not signed in). */
  user: User | null;
  /** True while loading initial auth state or Firebase config. */
  loading: boolean;
  /** Error message for auth failures. */
  error: string | null;
  /**
   * Section ids this user may open, as reported by the server.
   * `null` while still unknown (before /auth/me resolves).
   */
  allowedSections: string[] | null;
  /** Whether the user may open a given section. */
  canAccessSection: (sectionId: string) => boolean;
  /** Sign in with Google popup. */
  signIn: () => Promise<void>;
  /** Sign out. */
  signOut: () => Promise<void>;
  /** The Firestore root collection path (from server config). */
  firestoreRootCollection: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allowedSections, setAllowedSections] = useState<string[] | null>(null);
  const [firestoreRoot, setFirestoreRoot] = useState('product_dashboard');
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [devMode, setDevMode] = useState(false);

  // 1. Fetch config & init Firebase (or enter dev mode)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const config = await api.get<AuthConfigResponse>(endpoints.auth.config());

        if (cancelled) return;

        setFirestoreRoot(config.firestoreRootCollection);

        // Dev mode — skip Firebase auth entirely
        if (config.devMode || !config.firebaseConfig) {
          console.info('[Auth] Dev mode — skipping Firebase auth');
          setDevMode(true);
          setUser({ displayName: 'Dev User', email: 'dev@localhost' } as unknown as User);
          setAllowedSections(ALL_SECTION_IDS);
          setLoading(false);
          return;
        }

        // Initialize Firebase
        initFirebase(config.firebaseConfig);
        setFirebaseReady(true);
      } catch (err) {
        if (!cancelled) {
          console.error('[Auth] Failed to fetch Firebase config:', err);
          setError('Failed to load authentication configuration');
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // 2. Listen to auth state once Firebase is ready
  useEffect(() => {
    if (!firebaseReady) return;

    const auth = getFirebaseAuth();

    // Wire the API token provider to read the LIVE Firebase user at request
    // time, as soon as Firebase is ready.  This effect runs in an earlier
    // render than the one where `user`/`loading` flip, so by the time child
    // components (thread list, thread loader) fire their fetches the provider
    // is already in place.  Previously the provider was wired ONLY in the
    // `[user]` effect below — but React runs child effects before parent
    // effects, so on a hard refresh the children fetched with no token
    // (401 → empty), and threads only reappeared after a remount (section
    // switch).  Reading `auth.currentUser` live also avoids closing over a
    // stale user across token refreshes.
    setApiTokenProvider(async () => {
      try {
        return (await auth.currentUser?.getIdToken()) ?? null;
      } catch {
        return null;
      }
    });

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setAllowedSections(null);
        setLoading(false);
        return;
      }

      // Ask the server what this account may do. Access can be granted by email
      // domain OR by an explicit invite, so only the server can decide.
      (async () => {
        try {
          const me = await api.get<MeResponse>(endpoints.auth.me());
          setUser(fbUser);
          setAllowedSections(me.sections);
          setError(null);
        } catch (err) {
          const denied = err instanceof ApiRequestError && err.status === 403;
          if (denied) {
            await firebaseSignOut(auth);
            setUser(null);
            setAllowedSections(null);
            setError(err.message);
          } else {
            // Network or server trouble: don't lock the user out over it, but
            // leave sections unknown so nothing is silently hidden.
            console.error('[Auth] Failed to load account access:', err);
            setUser(fbUser);
            setAllowedSections(null);
            setError(null);
          }
        } finally {
          setLoading(false);
        }
      })();
    });

    return unsubscribe;
  }, [firebaseReady]);

  // Wire up the api.ts token provider so all API calls include auth
  useEffect(() => {
    if (user && 'getIdToken' in user && typeof user.getIdToken === 'function') {
      setApiTokenProvider(async () => {
        try {
          return await user.getIdToken();
        } catch {
          return null;
        }
      });
    } else {
      setApiTokenProvider(async () => null);
    }
  }, [user]);

  // Sign in with Google
  const signIn = useCallback(async () => {
    if (devMode) return; // no-op in dev mode
    if (!firebaseReady) return;
    setError(null);

    try {
      const auth = getFirebaseAuth();
      const provider = new GoogleAuthProvider();

      // No `hd` hint: it restricts the Google account chooser to one domain,
      // which would make invited external accounts unselectable. The auth-state
      // listener above verifies the account against the server instead.
      await signInWithPopup(auth, provider);
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') return;
      if (err.code === 'auth/cancelled-popup-request') return;
      console.error('[Auth] Sign-in error:', err);
      setError(err.message ?? 'Sign-in failed');
    }
  }, [devMode, firebaseReady]);

  // Sign out
  const signOut = useCallback(async () => {
    if (devMode) return; // no-op in dev mode
    if (!firebaseReady) return;
    try {
      const auth = getFirebaseAuth();
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('[Auth] Sign-out error:', err);
    }
  }, [devMode, firebaseReady]);

  // Until the server has answered, treat access as unrestricted so a slow or
  // failed /auth/me never hides sections from a user who is entitled to them.
  // The API still enforces the real boundary on every request.
  const canAccessSection = useCallback(
    (sectionId: string) => allowedSections === null || allowedSections.includes(sectionId),
    [allowedSections],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        allowedSections,
        canAccessSection,
        signIn,
        signOut,
        firestoreRootCollection: firestoreRoot,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
